import React, { useState } from "react";
import { Box, Typography, Button, IconButton, Popover, Avatar, List, ListItemButton } from "@mui/material";
// Chevron on iconsax (2026-09-04, matching the AI Agents pass) -- Close
// stays plain MUI, the app-wide "simple cross" dialog/chip-remove convention.
import { ArrowDown2 } from "iconsax-react";
import CloseIcon from "@mui/icons-material/Close";
import { reportsFilterPillSx, reportsDropdownPaperSx, reportsDropdownSelectedSx } from "./reportsFilterStyles";

// Reusable "[Label] [Any|Member name]" filter control -- same pill Button
// styling as Date Range/Add filter (reportsFilterPillSx) so it reads as one
// consistent control family in the bar, not a mismatched MUI Chip. Click
// the pill to open a popover (Any + every member, avatar/name/email) and
// pick one. `onRemove` is optional -- only multi-filter categories (2+
// fields) pass it, rendering a small separate X beside the pill; a
// single-filter category's always-on control has nothing to remove it back
// to (see CategoryReportDetail.jsx). `value` is a member object or null
// ("Any").
const CategoryMemberFilterChip = ({ label, value, members, onChange, onRemove }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSelect = (member) => {
    onChange(member);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<Box sx={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center" }}><ArrowDown2 size="100%" color="currentColor" /></Box>}
        sx={reportsFilterPillSx}
      >
        {label} {value ? value.name : "Any"}
      </Button>
      {onRemove && (
        <IconButton size="small" onClick={onRemove} sx={{ p: 0.4, color: "text.secondary" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { mt: 0.75, ...reportsDropdownPaperSx },
          },
        }}
      >
        <List sx={{ minWidth: 260, maxWidth: 320, maxHeight: 360, overflowY: "auto", py: 0 }}>
          <ListItemButton
            selected={!value}
            onClick={() => handleSelect(null)}
            sx={{
              py: 0.85,
              px: 1.625,
              mx: 0.5,
              mb: 0.25,
              borderRadius: "8px",
              "&.Mui-selected, &.Mui-selected:hover": reportsDropdownSelectedSx,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Any</Typography>
          </ListItemButton>
          {(members || []).map((m) => (
            <ListItemButton
              key={m._id}
              selected={value?._id === m._id}
              onClick={() => handleSelect(m)}
              sx={{
                py: 0.6,
                px: 1.625,
                mx: 0.5,
                mb: 0.25,
                gap: 1.25,
                borderRadius: "8px",
                "&.Mui-selected, &.Mui-selected:hover": reportsDropdownSelectedSx,
              }}
            >
              <Avatar sx={{ width: 28, height: 28, fontSize: "0.8rem" }}>
                {m.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: "text.primary" }} noWrap>
                  {m.name}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }} noWrap>
                  {m.email}
                </Typography>
              </Box>
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </Box>
  );
};

export default CategoryMemberFilterChip;
